import React, { useState, useEffect, useRef, useMemo } from "react";
import { useJobFlowTracking } from "../../hooks/useJobFlowTracking";
import { HiSparkles } from "react-icons/hi2";
import { LuPenLine, LuUpload, LuStar } from "react-icons/lu";
import { FaChevronLeft } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  generateJobFromPrompt,
  extractJobDetailsFromDoc,
  fetchJobs,
  updateJob,
  submitJob,
  fetchJobById,
  generateJobDescriptionWithAI,
  enhanceJobDescriptionWithAI,
  fetchAllSkills,
  createSkill,
} from "../../Redux/ActionCreators/jobsAction";
import { userDetails } from "../../Redux/ActionCreators/authAction";
import CreatableSelect from "react-select/creatable";
import ReactFlagsSelect from "react-flags-select";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Snackbar, Alert } from "@mui/material";
import countriesData from "../../utils/countries.json";
import citiesData from "../../utils/cities.json";
import classNames from "classnames";

const countriesList =
  countriesData[
    "select id, name, iso3, numeric_code, iso2, emoji  from countries where flag =1"
  ] || [];
const citiesList =
  citiesData["select id, name, country_id from cities where flag = 1"] || [];

const getDefaultCity = (countryIso2) => {
  const country = countriesList.find((c) => c.iso2 === countryIso2);
  return country
    ? citiesList.find((city) => city.country_id === country.id)?.name || ""
    : "";
};

const CREATE_MODES = [
  {
    key: "prompt",
    label: "Create with Prompt",
    icon: <HiSparkles size={26} />,
  },
  { key: "manually", label: "Create Manually", icon: <LuPenLine size={26} /> },
  { key: "upload", label: "Upload Job File", icon: <LuUpload size={26} /> },
  { key: "saved", label: "Saved Jobs", icon: <LuStar size={26} /> },
];

const SAMPLE_PROMPTS = [
  "Hiring a Human Resources Manager with 5+ years of experience in recruitment, employee engagement, and HR policies.",
  "Looking for a Digital Marketing Manager with 3+ years of expertise in SEO, Google Ads, and campaign analytics.",
  "Hiring a Customer Support Executive with 3+ years of experience, strong communication skills for email, chat, and phone support.",
];

const RESUME_OPTIONS = {
  "resume_human_resources_manager.pdf": {
    file_name: "resume_human_resources_manager.pdf",
    url: "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314110652_resume_human_resources_manager.pdf",
  },
  "resume_digital_marketing_lead.pdf": {
    file_name: "resume_digital_marketing_lead.pdf",
    url: "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314105624_resume_digital_marketing_lead.pdf",
  },
  "resume_junior_react_developer.pdf": {
    file_name: "resume_junior_react_developer.pdf",
    url: "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314105624_resume_junior_react_developer.pdf",
  },
  "resume_ui_ux_designer.pdf": {
    file_name: "resume_ui_ux_designer.pdf",
    url: "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314105624_resume_ui_ux_designer.pdf",
  },
  "resume_customer_support_executive.pdf": {
    file_name: "resume_customer_support_executive.pdf",
    url: "https://gappeo.blob.core.windows.net/dev-gappeoai/20260314110805_resume_customer_support_executive.pdf",
  },
};

const DEFAULT_INVITE_RESUME = RESUME_OPTIONS["resume_human_resources_manager.pdf"];

// Maps each SAMPLE_PROMPTS index to a resume
const SAMPLE_PROMPT_RESUME_MAP = [
  RESUME_OPTIONS["resume_human_resources_manager.pdf"],   // "Hiring a Human Resources Manager..."
  RESUME_OPTIONS["resume_digital_marketing_lead.pdf"],    // "Looking for a Digital Marketing Manager..."
  RESUME_OPTIONS["resume_customer_support_executive.pdf"], // "Hiring a Customer Support Executive..."
];

// Maps popular job titles to resumes
const POPULAR_JOB_RESUME_MAP = {
  "Digital Marketing Lead": RESUME_OPTIONS["resume_digital_marketing_lead.pdf"],
  "Junior React Developer": RESUME_OPTIONS["resume_junior_react_developer.pdf"],
  "UI UX Designer":         RESUME_OPTIONS["resume_ui_ux_designer.pdf"],
};

// Returns the best matching resume for a given job title string (used in both popular-job clicks and edit flow)
const getResumeByJobTitle = (title) => {
  if (!title) return DEFAULT_INVITE_RESUME;
  const t = title.toLowerCase();
  if (t.includes("digital marketing")) return RESUME_OPTIONS["resume_digital_marketing_lead.pdf"];
  if (t.includes("react") || t.includes("frontend") || t.includes("front-end")) return RESUME_OPTIONS["resume_junior_react_developer.pdf"];
  if ((t.includes("ui") && t.includes("ux")) || t.includes("ux designer") || t.includes("ui designer")) return RESUME_OPTIONS["resume_ui_ux_designer.pdf"];
  if (t.includes("customer support")) return RESUME_OPTIONS["resume_customer_support_executive.pdf"];
  if (t.includes("human resources") || t.includes("hr manager")) return RESUME_OPTIONS["resume_human_resources_manager.pdf"];
  return DEFAULT_INVITE_RESUME;
};

const POPULAR_JOBS = [
  {
    job_title: "Digital Marketing Lead",
    job_type: "Full-time",
    skills_required: [
      "Digital marketing strategy",
      "SEO",
      "SEM",
      "Google Analytics",
      "Social media marketing",
      "Email marketing",
      "Content marketing",
    ],
    job_qualification:
      "Bachelor's degree in Marketing, Business, Communications, or related field",
    job_description:
      "Job Title: Digital Marketing Lead\nSkills: Digital marketing strategy, SEO, SEM, Google Analytics, Social media marketing, Email marketing, Content marketing\nKey Responsibilities:\n- Develop and execute end-to-end digital marketing strategies across paid and organic channels\n- Lead campaign planning, execution, and optimization to drive lead generation and revenue growth\n- Analyze performance metrics using tools like Google Analytics to optimize ROI and CAC\n- Collaborate with sales, product, and design teams to ensure consistent brand messaging\n- Manage and mentor a small team or external agencies to deliver high-quality marketing initiatives\nMandatory Qualifications:\n- Proven experience leading multi-channel digital marketing campaigns in a B2B or B2C environment\n- Strong hands-on expertise with SEO/SEM tools and analytics platforms (e.g., Google Analytics, Google Ads)\n- Demonstrated ability to manage budgets and optimize campaigns based on performance data\n- Excellent communication and stakeholder management skills\n- Bachelor's degree in Marketing, Business, Communications, or a related discipline",
  },
  {
    job_title: "Junior React Developer",
    job_type: "Full-time",
    skills_required: ["React", "JavaScript", "TypeScript", "HTML", "CSS", "REST APIs", "Git"],
    job_qualification:
      "Bachelor's degree in Computer Science, Software Engineering, or equivalent practical experience",
    job_description:
      "Job Title: Junior React Developer\nSkills: React, JavaScript, TypeScript, HTML, CSS, REST APIs, Git\nKey Responsibilities:\n- Implement responsive user interfaces using React, JavaScript/TypeScript, HTML, and CSS\n- Collaborate with senior developers to translate UI/UX designs and requirements into reusable components\n- Integrate front-end components with RESTful APIs and backend services\n- Participate in code reviews, testing, and debugging to maintain code quality\n- Contribute to documentation and follow established coding standards and best practices\nMandatory Qualifications:\n- Foundational knowledge of React and modern JavaScript (ES6+)\n- Understanding of web fundamentals including HTML5, CSS3, and responsive design\n- Familiarity with Git or other version control systems\n- Ability to read and work with REST APIs and JSON data\n- Degree in Computer Science/Software Engineering or equivalent practical, project-based experience",
  },
  {
    job_title: "UI UX Designer",
    job_type: "Full-time",
    skills_required: [
      "UI design",
      "UX design",
      "Wireframing",
      "Prototyping",
      "User research",
      "Figma",
      "Design systems",
    ],
    job_qualification:
      "Bachelor's degree in Design, Human-Computer Interaction, or related field",
    job_description:
      "Job Title: UI UX Designer\nSkills: UI design, UX design, Wireframing, Prototyping, User research, Figma, Design systems\nKey Responsibilities:\n- Translate product requirements into intuitive user flows, wireframes, and high-fidelity UI designs\n- Conduct user research, interviews, and usability testing to validate design decisions\n- Create interactive prototypes using tools such as Figma to communicate design intent\n- Collaborate closely with product managers and engineers to ensure feasibility and consistency\n- Contribute to and maintain a scalable design system and component library\nMandatory Qualifications:\n- Demonstrated experience designing web or mobile products, supported by a strong portfolio\n- Proficiency in modern design and prototyping tools (e.g., Figma, Sketch, Adobe XD)\n- Solid understanding of user-centered design principles, accessibility, and responsive design\n- Ability to interpret user research findings and analytics to iterate on designs\n- Formal education in Design, HCI, or related field, or equivalent hands-on experience",
  },
];

function Stage1AddJobDetails({ onNext, onTransitionStart, onTransitionError, initialJobId, onResumeSelect, flowSessionId }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedMode, setSelectedMode] = useState("prompt");
  const [prompt, setPrompt] = useState("");
  const [randomPlaceholder] = useState(() => SAMPLE_PROMPTS[Math.floor(Math.random() * SAMPLE_PROMPTS.length)]);
  const [subStep, setSubStep] = useState(1);
  const fileInputRef = useRef();
  const isDraftAction = useRef(false);
  const isSavedJobMode = useRef(false);

  const latestJob = useSelector((state) => state.jobs.latestJob);
  const allJobs = useSelector((state) => state.jobs.jobList);
  const allSkills = useSelector((state) => state.jobs.skills);
  const meDetails = useSelector((state) => state.auth.meDetails);
  const hasAnyJobs = useSelector((state) => state.jobs?.hasAnyJobs);
  const [skillOptions, setSkillOptions] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  const { trackComplete, trackAbandon } = useJobFlowTracking({
    flowSessionId,
    stepId: 'add_job_details',
    stepLabel: 'Add Job Details',
    jobId,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSavedJob, setSelectedSavedJob] = useState(null);
  const hasFetchedDraft = useRef(false);

  useEffect(() => {
    dispatch(fetchAllSkills());
    dispatch(userDetails());
  }, [dispatch]);

  useEffect(() => {
    const draftJobId = location.state?.jobId;
    const passedSubStep = location.state?.subStep;
    if (draftJobId && passedSubStep === 2 && !hasFetchedDraft.current) {
      hasFetchedDraft.current = true;
      handleEditDraft(draftJobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.jobId, location.state?.subStep]);

  // When coming back from Stage 2 (parent has a saved jobId), restore the form at subStep 2
  useEffect(() => {
    if (initialJobId && !hasFetchedDraft.current) {
      hasFetchedDraft.current = true;
      handleEditDraft(initialJobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJobId]);

  const handleEditDraft = async (draftJobId) => {
    setLoading(true);
    const job = await dispatch(fetchJobById(draftJobId));
    if (job) {
      if (onResumeSelect) onResumeSelect(getResumeByJobTitle(job.title));
      setJobId(job.id);
      const countryIso =
        countriesList.find((c) => c.id === (job.country?.id || job.country_id))
          ?.iso2 || "US";
      await formik.setValues({
        ...formik.initialValues,
        title: job.title || "",
        company_name: job.company_name || "",
        job_type:
          job.job_type?.name === "Full-time"
            ? "Full Time"
            : job.job_type?.name || "Full Time",
        country: countryIso,
        skills: Array.isArray(job.skills)
          ? job.skills.map((s) => s.name || s)
          : [],
        city: job.city?.name || getDefaultCity(countryIso),
        experience_required: job.experience_required || "",
        salary: job.salary || "",
        qualifications: job.qualifications || "",
        description: job.description || "",
      });
      setSelectedMode("manually");
      setSubStep(2);
    } else {
      setSnackbar({
        open: true,
        message: "Failed to load job details.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (allSkills) {
      setSkillOptions(allSkills.map((s) => ({ value: s.name, label: s.name })));
    }
  }, [allSkills]);

  useEffect(() => {
    dispatch(fetchJobs(1, 100, "14107328-840b-4ad0-904f-8a2243720f70"));
  }, [dispatch]);

  useEffect(() => {
    if (hasAnyJobs === undefined && Array.isArray(allJobs)) {
      dispatch({ type: "SET_HAS_ANY_JOBS", payload: allJobs.length > 0 });
    }
  }, [allJobs, hasAnyJobs, dispatch]);

  const jobTypeIdMap = {
    "Part Time": "64b3c119-597e-4f8a-a66a-b448fc2baf44",
    "Full Time": "9c4ae6ff-a326-4ddb-936e-042cdd715bc2",
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Job title is required"),
    company_name: Yup.string().required("Company name is required"),
    job_type: Yup.string().required("Job type is required"),
    country: Yup.string().required("Country is required"),
    city: Yup.string().required("City is required"),
    experience_required: Yup.string().required("Experience is required"),
    qualifications: Yup.string(),
    skills: Yup.array().min(1, "At least one skill is required"),
    description: Yup.string().required("Description is required"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      company_name: "",
      job_type: "",
      skills: [],
      country: "US",
      city: getDefaultCity("US"),
      experience_required: "",
      salary: "",
      description: "",
      qualifications: "",
      additional_input: "",
      autoGenerate: false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const selectedCountry = countriesList.find(
        (c) => c.iso2 === values.country,
      );
      const selectedCity = citiesList.find(
        (city) =>
          city.name === values.city && city.country_id === selectedCountry?.id,
      );
      const skillIds = values.skills
        .map((name) => allSkills.find((s) => s.name === name)?.id)
        .filter(Boolean);

      const payload = {
        experience_required: values.experience_required,
        salary: values.salary,
        qualifications: values.qualifications,
        job_type_id: jobTypeIdMap[values.job_type],
        job_type: values.job_type,
        country_id: selectedCountry?.id || null,
        city_id: selectedCity?.id || null,
        country: selectedCountry?.name || values.country,
        city: selectedCity?.name || values.city,
        description: values.description,
        skills: values.skills,
        skill_ids: skillIds,
        is_active: true,
        url: `https://demo.gappeo.ai/jobs/[job_id]/apply`,
        job_status_id: "855705f5-9696-46fc-b402-4a18ffd819af",
        company_name: values.company_name,
        title: values.title,
        additional_input: { voice_call_description: values.additional_input },
        job_type_text: values.job_type,
        job_location_city_text: selectedCity?.name || values.city,
        job_location_country_text: selectedCountry?.name || values.country,
      };

      let currentJobId = isSavedJobMode.current ? null : jobId || latestJob?.id;

      // Show the next stage skeleton immediately, before the dispatch.
      // trackComplete must be called first — onTransitionStart unmounts this component.
      if (!isDraftAction.current) {
        trackComplete(jobId || latestJob?.id || null);
        if (onTransitionStart) onTransitionStart("Creating AI Features, Please Wait....");
      }

      try {
        if (currentJobId) {
          const result = await dispatch(updateJob(currentJobId, payload));
          if (result?.__error402) {
            setSubmitting(false);
            setSnackbar({
              open: true,
              message: "Active jobs limit reached.",
              severity: "warning",
            });
            if (!isDraftAction.current && onTransitionError) onTransitionError();
            return;
          }
        } else {
          const newJobId = await dispatch(submitJob(payload));
          if (newJobId?.__error402) {
            setSubmitting(false);
            setSnackbar({
              open: true,
              message: "Active jobs limit reached.",
              severity: "warning",
            });
            if (!isDraftAction.current && onTransitionError) onTransitionError();
            return;
          }
          if (typeof newJobId === "string") {
            currentJobId = newJobId;
          } else if (newJobId && newJobId.id) {
            currentJobId = newJobId.id;
          }
        }

        if (!isDraftAction.current && onNext) {
          // Dispatch succeeded — clear skeleton and pass the real jobId to parent
          onNext(currentJobId, true);
        } else if (isDraftAction.current) {
          onNext(currentJobId, true);
          setSubmitting(false);
          setSnackbar({
            open: true,
            message: "Job saved successfully.",
            severity: "success",
          });
        }
      } catch (err) {
        if (!isDraftAction.current && onTransitionError) onTransitionError();
      }
    },
  });

  const countryOptions = useMemo(() => {
    return countriesList.map((c) => ({
      label: `${c.emoji} ${c.iso3}`,
      value: c.iso2,
      id: c.id,
      iso3: c.iso3,
    }));
  }, []);

  const cityOptions = useMemo(() => {
    const selectedCountry = countriesList.find(
      (c) => c.iso2 === formik.values.country,
    );
    if (!selectedCountry) return [];
    return citiesList
      .filter((city) => city.country_id === selectedCountry.id)
      .map((city) => ({
        label: city.name,
        value: city.name,
        id: city.id,
      }));
  }, [formik.values.country]);

  const handleModeSelect = (key) => {
    // Reset old data context
    formik.resetForm();
    setJobId(null);
    setSelectedFile(null);
    setSelectedSavedJob(null);
    setPrompt("");
    isSavedJobMode.current = false;
    if (onResumeSelect) onResumeSelect(DEFAULT_INVITE_RESUME);

    setSelectedMode(key);
    if (key === "manually") {
      setSubStep(2);
    } else {
      setSubStep(1);
    }
  };

  const handleBackToPrompt = () => {
    // Clear all previous job details
    formik.resetForm();
    setJobId(null);
    setSelectedFile(null);
    setSelectedSavedJob(null);
    setPrompt("");
    isSavedJobMode.current = false;
    if (onResumeSelect) onResumeSelect(DEFAULT_INVITE_RESUME);

    setSelectedMode("prompt");
    setSubStep(1);
  };

  const handleGeneratePromt = async () => {
    if (!prompt) return;
    // Determine resume: use mapped sample prompt resume if prompt matches, else default
    const promptIndex = SAMPLE_PROMPTS.indexOf(prompt);
    if (onResumeSelect) {
      onResumeSelect(promptIndex >= 0 ? SAMPLE_PROMPT_RESUME_MAP[promptIndex] : DEFAULT_INVITE_RESUME);
    }
    setLoading(true);
    setSubStep(2);
    const res = await dispatch(generateJobFromPrompt({ query: prompt }));
    if (res && res.job_details) {
      const jd = res.job_details;
      const matchedSkills = jd.skills_required || [];
      const defaultCompanyName =
        meDetails?.companies?.length > 0 ? meDetails.companies[0].name : "";
      const countryIso = jd.job_location_iso || "US";

      await formik.setValues({
        ...formik.initialValues,
        title: jd.job_title || "",
        company_name: jd.company_name || defaultCompanyName,
        job_type:
          jd.job_type === "Full-time"
            ? "Full Time"
            : jd.job_type || "Full Time",
        country: countryIso,
        skills: matchedSkills,
        city: jd.job_location_city || getDefaultCity(countryIso),
        experience_required: jd.experience_required || "1 year",
        salary: jd.job_salary || "",
        qualifications: jd.job_qualification || "",
        description: jd.job_description || "",
      });
    } else {
      setSnackbar({
        open: true,
        message: "Failed to generate job from prompt.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handlePopularJobClick = (job) => {
    if (onResumeSelect) onResumeSelect(POPULAR_JOB_RESUME_MAP[job.job_title] || getResumeByJobTitle(job.job_title));
    formik.resetForm();
    setJobId(null);
    isSavedJobMode.current = false;
    const countryIso = "US";
    const defaultCompanyName =
      meDetails?.companies?.length > 0 ? meDetails.companies[0].name : "";
    formik.setValues({
      ...formik.initialValues,
      title: job.job_title,
      company_name: defaultCompanyName,
      job_type: job.job_type === "Full-time" ? "Full Time" : job.job_type || "Full Time",
      country: countryIso,
      skills: job.skills_required,
      city: getDefaultCity(countryIso),
      experience_required: "1 year",
      salary: "",
      qualifications: job.job_qualification,
      description: job.job_description,
    });
    setSelectedMode("manually");
    setSubStep(2);
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setSubStep(2);
    const formData = new FormData();
    formData.append("file", selectedFile);
    const res = await dispatch(extractJobDetailsFromDoc(formData));
    if (res) {
      const matchedSkills = res.skills_required || [];
      const countryIso = res.job_location_iso || "US";

      await formik.setValues({
        ...formik.initialValues,
        title: res.job_title || "",
        company_name: res.company_name || "",
        job_type:
          res.job_type === "Full-time"
            ? "Full Time"
            : res.job_type || "Full Time",
        country: countryIso,
        skills: matchedSkills,
        city: res.job_location_city || getDefaultCity(countryIso),
        experience_required: res.experience_required || "",
        salary: res.job_salary || "",
        qualifications: res.job_qualification || "",
        description: res.job_description || "",
      });
    } else {
      setSnackbar({
        open: true,
        message: "Failed to extract job details.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleSavedJobCreate = async () => {
    if (!selectedSavedJob) return;
    setLoading(true);
    setSubStep(2);
    isSavedJobMode.current = true;
    const job = await dispatch(fetchJobById(selectedSavedJob.value));
    if (job) {
      // Do NOT set jobId — we want to create a new job, not update the saved one
      const countryIso =
        countriesList.find((c) => c.id === (job.country?.id || job.country_id))
          ?.iso2 || "US";
      await formik.setValues({
        ...formik.initialValues,
        title: job.title || "",
        company_name: job.company_name || "",
        job_type:
          job.job_type?.name === "Full-time"
            ? "Full Time"
            : job.job_type?.name || "Full Time",
        country: countryIso,
        skills: Array.isArray(job.skills)
          ? job.skills.map((s) => s.name || s)
          : [],
        city: job.city?.name || getDefaultCity(countryIso),
        experience_required: job.experience_required || "",
        salary: job.salary || "",
        qualifications: job.qualifications || "",
        description: job.description || "",
      });
    } else {
      setSnackbar({
        open: true,
        message: "Failed to load saved job details.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleGenerateAI = async () => {
    let currentJobId = jobId || latestJob?.id;

    setAiGenerating(true);

    const aiPayload = {
      experience_required: formik.values.experience_required,
      salary: formik.values.salary,
      qualifications: formik.values.qualifications,
      job_type: formik.values.job_type,
      location: formik.values.country,
      responsibilities: "",
      skills: formik.values.skills,
      company_name: formik.values.company_name,
      title: formik.values.title,
      description: formik.values.description,
    };

    try {
      // If job hasn't been saved yet, save it first before generating
      if (!currentJobId) {
        const selectedCountry = countriesList.find(
          (c) => c.iso2 === formik.values.country,
        );
        const selectedCity = citiesList.find(
          (city) =>
            city.name === formik.values.city &&
            city.country_id === selectedCountry?.id,
        );
        const skillIds = formik.values.skills
          .map((name) => allSkills.find((s) => s.name === name)?.id)
          .filter(Boolean);

        const payload = {
          experience_required: formik.values.experience_required,
          salary: formik.values.salary,
          qualifications: formik.values.qualifications,
          job_type_id: jobTypeIdMap[formik.values.job_type],
          job_type: formik.values.job_type,
          country_id: selectedCountry?.id || null,
          city_id: selectedCity?.id || null,
          country: selectedCountry?.name || formik.values.country,
          city: selectedCity?.name || formik.values.city,
          description: formik.values.description,
          skills: formik.values.skills,
          skill_ids: skillIds,
          is_active: true,
          url: `https://demo.gappeo.ai/jobs/[job_id]/apply`,
          job_status_id: "855705f5-9696-46fc-b402-4a18ffd819af",
          company_name: formik.values.company_name,
          title: formik.values.title,
          additional_input: { voice_call_description: formik.values.additional_input },
          job_type_text: formik.values.job_type,
          job_location_city_text: selectedCity?.name || formik.values.city,
          job_location_country_text: selectedCountry?.name || formik.values.country,
        };

        const newJobId = await dispatch(submitJob(payload));
        if (newJobId?.__error402) {
          setSnackbar({
            open: true,
            message: "Active jobs limit reached.",
            severity: "warning",
          });
          setAiGenerating(false);
          return;
        }
        if (typeof newJobId === "string") {
          currentJobId = newJobId;
        } else if (newJobId && newJobId.id) {
          currentJobId = newJobId.id;
        }
        if (currentJobId) setJobId(currentJobId);
      }

      const attemptsLeft = latestJob?.generate_description_quota_left ?? 3;

      if (attemptsLeft === 0) {
        setSnackbar({
          open: true,
          message: "AI generation limit reached.",
          severity: "warning",
        });
        setAiGenerating(false);
        return;
      }

      let aiResponse;
      if (attemptsLeft < 3) {
        aiResponse = await dispatch(
          enhanceJobDescriptionWithAI(aiPayload, currentJobId),
        );
      } else {
        aiResponse = await dispatch(
          generateJobDescriptionWithAI(aiPayload, currentJobId),
        );
      }

      if (aiResponse?.description) {
        const cleanDescription = aiResponse.description.replace(/\*\*/g, "");
        formik.setFieldValue("description", cleanDescription);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Something went wrong while generating description.",
        severity: "error",
      });
    }
    setAiGenerating(false);
  };

  const isBusy = formik.isSubmitting || loading;

  if (subStep === 2) {
    return (
      <div className="cj-stage1" style={{ alignItems: "flex-start" }}>
        <div className="cj-preview-header">
          {isBusy ? (
            <p className="gradient-text-loading">
              Creating Job, Please Wait....
            </p>
          ) : (
            <div>
              {!jobId && (
                <button
                  className="cj-gray-link"
                  type="button"
                  onClick={handleBackToPrompt}
                >
                  <FaChevronLeft /> Back to Prompt
                </button>
              )}
              <h3 className="cj-preview-title">Preview Job</h3>
              <p className="cj-preview-subtitle">
                You can edit needed fields or continue to select AI Features
              </p>
            </div>
          )}
          {!isBusy &&
            <button
                className="cj-btn-primary"
                type="button"
                onClick={(e) => {
                isDraftAction.current = false;
                formik.handleSubmit(e);
                }}
                disabled={isBusy}
            >
                {isBusy ? "Updating..." : "Next: Select AI Features"}{" "}
                <span className="cj-btn-arrow">→</span>
            </button>
          }
        </div>

        <form
          onSubmit={(e) => {
            isDraftAction.current = false;
            formik.handleSubmit(e);
          }}
          style={{ width: "100%" }}
        >
          <div
            className="create-job-body"
            style={{ padding: 0, margin: 0, boxShadow: "none" }}
          >
            <div className="row" style={{ width: "100%", margin: 0 }}>
              <div className="col-md-5 pl-0 pb-3" style={{ paddingLeft: "0" }}>
                <div className="form-section">
                  <label>
                    Job title <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    className={classNames({ "gradient-border-wrap": isBusy })}
                  >
                    <input
                      type="text"
                      name="title"
                      placeholder="Enter job title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={classNames("form-control", {
                        "input-error":
                          formik.touched.title && formik.errors.title,
                      })}
                      disabled={isBusy}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-5 pb-3">
                <div className="form-section">
                  <label>
                    Company name <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    className={classNames({ "gradient-border-wrap": isBusy })}
                  >
                    <input
                      type="text"
                      name="company_name"
                      placeholder="Enter company name"
                      value={formik.values.company_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={classNames("form-control", {
                        "input-error":
                          formik.touched.company_name &&
                          formik.errors.company_name,
                      })}
                      disabled={isBusy}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-5 pl-0 pb-3" style={{ paddingLeft: "0" }}>
                <div className="form-section">
                  <label>
                    Job type <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    className={classNames("mt-2", {
                      "gradient-border-wrap": isBusy,
                    })}
                  >
                    <select
                      className={classNames("form-control", {
                        "input-error":
                          formik.touched.job_type && formik.errors.job_type,
                      })}
                      name="job_type"
                      value={formik.values.job_type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isBusy}
                    >
                      <option hidden>Select job type</option>
                      <option value="Full Time">Full Time</option>
                      <option value="Part Time">Part Time</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-md-5 pb-3">
                <div className="form-section">
                  <label>
                    Skills required <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    className={classNames("mt-2", {
                      "gradient-border-wrap": isBusy,
                    })}
                  >
                    <CreatableSelect
                      isMulti
                      className={classNames("select", {
                        "input-error":
                          formik.touched.skills && formik.errors.skills,
                      })}
                      classNamePrefix="react-select"
                      name="skills"
                      placeholder="Select or type to add"
                      options={skillOptions}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      onChange={async (selectedOptions) => {
                        const selectedNames = selectedOptions.map(
                          (opt) => opt.value,
                        );
                        formik.setFieldValue("skills", selectedNames);

                        const existingNames = allSkills.map((s) => s.name);
                        const newSkills = selectedNames.filter(
                          (name) => !existingNames.includes(name),
                        );

                        for (const skillName of newSkills) {
                          await dispatch(createSkill(skillName));
                        }
                      }}
                      onBlur={() => formik.setFieldTouched("skills", true)}
                      value={formik.values.skills.map((skill) => ({
                        value: skill,
                        label: skill,
                      }))}
                      isDisabled={isBusy}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-5 pl-0 pb-3" style={{ paddingLeft: "0" }}>
                <div className="form-section">
                  <label>
                    Job country <span style={{ color: "red" }}>*</span>
                  </label>
                  <div className="location-selects d-flex">
                    <div
                      className={classNames({ "gradient-border-wrap": isBusy })}
                      style={{ width: "100%" }}
                    >
                      <ReactFlagsSelect
                        selected={formik.values.country}
                        onSelect={(code) => {
                          formik.setFieldValue("country", code);
                          formik.setFieldValue("city", getDefaultCity(code));
                        }}
                        countries={countryOptions.map((c) => c.value)}
                        customLabels={Object.fromEntries(
                          countryOptions.map((c) => [c.name, `${c.iso3}`]),
                        )}
                        placeholder="Country"
                        className={classNames("country-flag-select", {
                          "input-error":
                            formik.touched.country && formik.errors.country,
                        })}
                        selectable={!isBusy}
                        searchable={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-5 pb-3">
                <div className="form-section">
                  <label>
                    Job city <span style={{ color: "red" }}>*</span>
                  </label>
                  <div className="location-selects d-flex">
                    <div
                      className={classNames({ "gradient-border-wrap": isBusy })}
                      style={{ width: "100%" }}
                    >
                      <Select
                        options={cityOptions}
                        isDisabled={isBusy}
                        placeholder="Select city"
                        className={classNames("city-select", {
                          "input-error":
                            formik.touched.city && formik.errors.city,
                        })}
                        classNamePrefix="react-select"
                        name="city"
                        value={
                          cityOptions.find(
                            (o) => o.value === formik.values.city,
                          ) || null
                        }
                        onChange={(option) =>
                          formik.setFieldValue(
                            "city",
                            option ? option.value : "",
                          )
                        }
                        onBlur={() => formik.setFieldTouched("city", true)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-5 pl-0 pb-3" style={{ paddingLeft: "0" }}>
                <div className="form-section">
                  <label>
                    Experience required <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    className={classNames({ "gradient-border-wrap": isBusy })}
                  >
                    <input
                      type="text"
                      name="experience_required"
                      placeholder="Enter experience required"
                      value={formik.values.experience_required}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={classNames("form-control", {
                        "input-error":
                          formik.touched.experience_required &&
                          formik.errors.experience_required,
                      })}
                      disabled={isBusy}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-5 pb-3">
                <div className="form-section">
                  <label>Job salary</label>
                  <div
                    className={classNames({ "gradient-border-wrap": isBusy })}
                  >
                    <input
                      type="text"
                      name="salary"
                      className="form-control"
                      placeholder="Enter salary range"
                      value={formik.values.salary}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isBusy}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-5 pl-0 pb-3" style={{ paddingLeft: "0" }}>
                <div className="form-section">
                  <label>Job qualifications</label>
                  <div
                    className={classNames({ "gradient-border-wrap": isBusy })}
                  >
                    <input
                      name="qualifications"
                      type="text"
                      className="form-control"
                      placeholder="Enter job qualifications"
                      value={formik.values.qualifications}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isBusy}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-10 pl-0" style={{ paddingLeft: "0" }}>
                <div className="form-section text-area">
                  <label style={{ width: "100%" }}>
                    <div className="d-flex justify-content-between">
                      <h6>
                        Job description <span style={{ color: "red" }}>*</span>
                      </h6>
                    </div>
                    <div
                      className={classNames({
                        "gradient-border-wrap": isBusy || aiGenerating,
                      })}
                    >
                      <textarea
                        name="description"
                        placeholder="Write text here ..."
                        value={formik.values.description || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={classNames({
                          "input-error":
                            formik.touched.description &&
                            formik.errors.description,
                        })}
                        disabled={isBusy || aiGenerating}
                        style={{
                          width: "100%",
                          display: "block",
                          borderBottom:'none'
                        }}
                      />
                    </div>
                    {!isBusy &&
                      <div className="ai-generate">
                        <div className="ai-generate-wrapper">
                          <button
                            type="button"
                            className="ai-generate-btn"
                            onClick={handleGenerateAI}
                            disabled={aiGenerating}
                          >
                            <img src="/images/ai-icon.png" alt="" />
                            &nbsp;
                            {aiGenerating
                              ? "Creating..."
                              : latestJob?.generate_description_quota_left >= 3
                                ? "Create job description"
                                : latestJob?.generate_description_quota_left > 0
                                  ? `Recreate job description (${latestJob.generate_description_quota_left} left)`
                                  : "Create job description"}
                          </button>
                        </div>
                      </div>
                    }
                  </label>
                </div>
              </div>
            </div>
          </div>
        </form>

        {!isBusy &&
            <div className="cj-preview-footer">
                <button
                    className="cj-btn-primary"
                    type="button"
                    onClick={(e) => {
                    isDraftAction.current = false;
                    formik.handleSubmit(e);
                    }}
                    disabled={isBusy}
                >
                    {isBusy ? "Updating..." : "Next: Select AI Features"}{" "}
                    <span className="cj-btn-arrow">→</span>
                </button>
            </div>
        }
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </div>
    );
  }

  return (
    <div className="cj-stage1">
      <div>
        <h3 className="cj-prompt-title">Create a Job Description</h3>
        <p className="cj-prompt-disc">Choose how you'd like to start. Use AI for speed, saved templates for consistency, or manual entry for full control.</p>
      </div>
      <div className="cj-mode-grid">
        {CREATE_MODES.map((mode) => (
          <button
            key={mode.key}
            className={`cj-mode-card ${selectedMode === mode.key ? "cj-mode-card--active" : ""}`}
            onClick={() => handleModeSelect(mode.key)}
          >
            <span className="cj-mode-icon">{mode.icon}</span>
            <span className="cj-mode-label">{mode.label}</span>
          </button>
        ))}
      </div>

      {selectedMode === "prompt" && (
        <div className="cj-prompt-section">
          <div className="cj-textarea-wrapper">
            <textarea
              className="cj-textarea"
              placeholder={randomPlaceholder}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onFocus={() => {
                if (!prompt) setPrompt(randomPlaceholder);
              }}
              rows={5}
            />
          </div>

          <button
            className="cj-btn-primary"
            onClick={handleGeneratePromt}
            disabled={loading || !prompt}
          >
            {loading ? "Creating..." : "Create job"}{" "}
            <span className="cj-btn-arrow">→</span>
          </button>

          <p className="cj-popular-jobs">
            <strong>Popular Jobs:</strong>{" "}
            {POPULAR_JOBS.map((job, i) => (
              <span key={job.job_title}>
                <button
                  className="cj-popular-link"
                  onClick={() => handlePopularJobClick(job)}
                >
                  {job.job_title}
                </button>
                {i < POPULAR_JOBS.length - 1 && ", "}
              </span>
            ))}
          </p>
        </div>
      )}

      {selectedMode === "upload" && (
        <div className="cj-prompt-section">
          <div
            className="modal-input"
            style={{
              width: "100%",
              border: "2px dashed #ccc",
              padding: "2rem",
              textAlign: "center",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
              justifyContent: "center",
            }}
          >
            <input
              type="file"
              accept=".doc,.docx"
              ref={fileInputRef}
              onChange={(e) => setSelectedFile(e.target.files[0])}
              style={{ display: "none" }}
            />
            <img
              src="/images/upload-icon-pink.svg"
              alt="upload"
              style={{ width: 40, marginBottom: 10 }}
            />
            <p className="cj-popular-jobs" style={{ margin: "0" }}>
              Click to upload
            </p>
            <p className="cj-popular-jobs" style={{ margin: "0" }}>
              Supported format: .docx
            </p>
            <p className="cj-popular-jobs" style={{ margin: "0" }}>
              Only one .docx file can be selected at a time.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                marginTop: "1rem",
              }}
            >
              <button
                className="cj-btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                style={{
                  background: "transparent",
                  border: "1px solid #f75891",
                  color: "#f75891",
                  width: "unset",
                }}
              >
                Browse .docx
              </button>
            </div>
            {selectedFile && (
              <div style={{ marginTop: "1rem" }}>
                <p>
                  Selected File: <strong>{selectedFile.name}</strong>
                </p>
              </div>
            )}
          </div>
          <button
            className="cj-btn-primary"
            style={{ marginTop: "1rem" }}
            onClick={handleUploadSubmit}
            disabled={!selectedFile || loading}
          >
            {loading ? "Uploading..." : "Create job"}{" "}
            <span className="cj-btn-arrow">→</span>
          </button>
        </div>
      )}

      {selectedMode === "saved" && (
        <div className="cj-prompt-section">
          <div
            style={{ minWidth: "400px", width: "100%", marginBottom: "1rem" }}
          >
            <CreatableSelect
              options={
                allJobs && Array.isArray(allJobs)
                  ? allJobs.map((job) => ({
                      value: job.id,
                      label: job.title || "Untitled Job",
                    }))
                  : []
              }
              onChange={(selected) => setSelectedSavedJob(selected)}
              placeholder="Select a saved job..."
              isClearable
              styles={{
                container: (base) => ({ ...base, width: "100%" }),
                control: (base) => ({
                  ...base,
                  padding: "5px",
                  borderRadius: "8px",
                }),
              }}
            />
          </div>
          <button
            className="cj-btn-primary"
            onClick={handleSavedJobCreate}
            disabled={!selectedSavedJob || loading}
          >
            {loading ? "Loading..." : "Create job"}{" "}
            <span className="cj-btn-arrow">→</span>
          </button>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
}

export default Stage1AddJobDetails;
